import {useEffect, useState} from 'react'
import {Breadcrumb, Button, Col, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {useLazyQuery} from '@apollo/client'
import {useParams, Link} from 'react-router-dom'
import {toast} from 'react-toastify'
import moment from 'moment'
import {GET_ALL_PDF_ENVELOPES, GET_ONE_PDF_DOCUMENT_TEMPLATE} from '../../gql/queries/pdfEnvelopesApiQueries'
import {NodePdfEnvelope} from '../../types/pdfEnvelopesApi'
import {parseId} from '../../helpers'
import {useDisclourse} from '../../hooks/useDisclourse'
import {BadgeStatus} from '../pdfEnvelopes/components/BadgeStatus'
import {ModalEnvelopePreview} from '../pdfEnvelopes/components/ModalEnvelopePreview'

const emDash = '—'
const chainIcon = '⛓'
const boltIcon = '⚡'

export const PdfEnvelopeEntriesPage = () => {
  const intl = useIntl()
  const {id} = useParams<{id: string}>()

  const [gqlGetTemplate, {data: templateData}] = useLazyQuery(GET_ONE_PDF_DOCUMENT_TEMPLATE, {
    fetchPolicy: 'no-cache',
  })

  const [gqlGetEnvelopes, {data, loading, error, refetch}] = useLazyQuery(GET_ALL_PDF_ENVELOPES, {
    fetchPolicy: 'no-cache',
  })

  const [filterStatus, setFilterStatus] = useState('')
  const [selectedEnvelopeId, setSelectedEnvelopeId] = useState<string>('')
  const {isOpen, onOpen, onClose} = useDisclourse()

  const templateName = templateData?.pdfDocumentTemplateById?.name ?? id

  useEffect(() => {
    if (!id) return
    const numericId = parseId(id) || id
    gqlGetTemplate({variables: {id: numericId}})
    gqlGetEnvelopes({variables: {templateId: numericId, status: filterStatus || null}})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (error)
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: error.message}
        )
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const envelopes: NodePdfEnvelope[] =
    data?.allPdfEnvelopes ?? []

  const handleSearch = () => {
    if (!id) return
    gqlGetEnvelopes({variables: {templateId: parseId(id) || id, status: filterStatus || null}})
  }

  const signersSummary = (envelope: NodePdfEnvelope) => {
    const total = envelope.signers?.length ?? 0
    const signed = envelope.signers?.filter((s) => s.status === 'SIGNED').length ?? 0
    return intl.formatMessage(
      {id: 'documents.pdfEnvelopeEntries.signersSummary', defaultMessage: '{signed}/{total} signed'},
      {signed, total}
    )
  }

  const sentByLabel = (envelope: NodePdfEnvelope) =>
    envelope.user ? `${envelope.user.firstName} ${envelope.user.lastName}`.trim() : emDash

  return (
    <div className='signatureForms-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/pdf-envelopes'}} className='fs-3'>
            <FormattedMessage
              id='documents.pdfEnvelopes.page.breadcrumb'
              defaultMessage='PDF signing'
            />
          </Breadcrumb.Item>
          <Breadcrumb.Item active className='fs-3'>
            {templateName}
          </Breadcrumb.Item>
        </Breadcrumb>
      </header>

      <Row className='signatureForms'>
        <Col className='container'>
          <div className='signatureForms-container'>
            <div className='title-header'>
              <h3>
                {intl.formatMessage(
                  {id: 'documents.pdfEnvelopeEntries.title', defaultMessage: 'Envelopes — {name}'},
                  {name: templateName}
                )}
              </h3>
            </div>

            <div className='filters-header'>
              <InputGroup>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value=''>
                    {intl.formatMessage({id: 'documents.pdfEnvelopes.page.status.all', defaultMessage: 'All statuses'})}
                  </option>
                  <option value='PENDING'>
                    {intl.formatMessage({id: 'common.status.pending', defaultMessage: 'Pending'})}
                  </option>
                  <option value='PARTIAL'>
                    {intl.formatMessage({id: 'documents.pdfEnvelopeEntries.status.partial', defaultMessage: 'Partial'})}
                  </option>
                  <option value='COMPLETED'>
                    {intl.formatMessage({id: 'documents.pdfEnvelopeEntries.status.completed', defaultMessage: 'Completed'})}
                  </option>
                  <option value='VOIDED'>
                    {intl.formatMessage({id: 'documents.pdfEnvelopeEntries.status.voided', defaultMessage: 'Voided'})}
                  </option>
                  <option value='EXPIRED'>
                    {intl.formatMessage({id: 'documents.pdfEnvelopeEntries.status.expired', defaultMessage: 'Expired'})}
                  </option>
                </Form.Select>
                <Button variant='outline-success' style={{border: '1px solid #dbdfe9'}} onClick={handleSearch}>
                  <FormattedMessage
                    id='common.action.filter'
                    defaultMessage='Filter'
                  />
                </Button>
              </InputGroup>
            </div>

            <div className='table-responsive table table-striped table-hover'>
              <table className='table-scrollable table-scroll table table-striped table-hover' style={{whiteSpace: 'nowrap'}}>
                <thead>
                  <tr>
                    <th className='fw-bold'>
                      <FormattedMessage id='documents.pdfEnvelopeEntries.col.title' defaultMessage='Title' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.status' defaultMessage='Status' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='documents.pdfEnvelopes.request.signers' defaultMessage='Signers' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='documents.pdfEnvelopeEntries.col.order' defaultMessage='Order' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='documents.pdfEnvelopeEntries.col.sentBy' defaultMessage='Sent by' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.created' defaultMessage='Created' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                    </th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan={7}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {envelopes.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex flex-center' style={{height: '20vh'}}>
                            <FormattedMessage
                              id='documents.pdfEnvelopeEntries.empty'
                              defaultMessage='No envelopes for this template.'
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {envelopes.map((env) => (
                      <tr key={env.id}>
                        <td className='fw-semibold'>{env.title}</td>
                        <td><BadgeStatus state={env.status} /></td>
                        <td>
                          <span>{signersSummary(env)}</span>
                          <div style={{fontSize: 11, color: '#888'}}>
                            {env.signers?.map((s) => (
                              <span key={s.id} className='me-2'>
                                {s.recipientName} <BadgeStatus state={s.status} />
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span style={{fontSize: 12}}>
                            {env.signingOrder === 'SEQUENTIAL' ? (
                              <>
                                {chainIcon} <FormattedMessage
                                  id='documents.pdfEnvelopeEntries.order.sequential'
                                  defaultMessage='Sequential'
                                />
                              </>
                            ) : (
                              <>
                                {boltIcon} <FormattedMessage
                                  id='documents.pdfEnvelopeEntries.order.parallel'
                                  defaultMessage='Parallel'
                                />
                              </>
                            )}
                          </span>
                        </td>
                        <td>{sentByLabel(env)}</td>
                        <td>{env.created ? moment(env.created).format('DD MMM YY HH:mm') : emDash}</td>
                        <td>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            style={{border: '1px solid #3e97ff'}}
                            onClick={() => {
                              setSelectedEnvelopeId(env.id)
                              onOpen()
                            }}
                          >
                            <i className='bi bi-eye' /> <FormattedMessage
                              id='documents.pdfEnvelopeEntries.viewDetail'
                              defaultMessage='View detail'
                            />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && selectedEnvelopeId && (
        <ModalEnvelopePreview
          envelopeId={selectedEnvelopeId}
          isOpen={isOpen}
          onClose={onClose}
          refetch={refetch}
        />
      )}
    </div>
  )
}
